import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Plus, Minus, Search, X, Wrench, Package, Zap } from "lucide-react";

const formatServicePrice = (s) => {
  if (s.price_text) return s.price_text;
  if (!s.price || s.price === 0) return "Quote";
  const prefix = s.price_from ? "From " : "";
  return `${prefix}$${Number(s.price).toFixed(2)}`;
};

const moneyN = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Re-usable services + parts picker.
 * Props:
 *  - value: array of quote items { service_id|product_id, sku, name, price, cost, qty, kind }
 *  - onChange: (next) => void
 *  - tone: "light" (default, customer booking page — services only) | "dark" (admin panel — services + parts tabs)
 *
 * On the booking page (tone=light) only services are shown — customers can't add parts directly.
 * In the admin panel (tone=dark) we show a tab toggle so admin can add shop products at cost too.
 */
export const QuotePicker = ({ value = [], onChange, tone = "light" }) => {
  const isDark = tone === "dark";
  const [tab, setTab] = useState("services"); // "services" | "parts"

  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [vehicle, setVehicle] = useState("");

  useEffect(() => {
    const calls = [api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]))];
    if (isDark) {
      // Admin-only: load products with cost so admin can add parts to a quote.
      calls.push(api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => setProducts([])));
    }
    Promise.all(calls).finally(() => setLoading(false));
  }, [isDark]);

  const vehicles = useMemo(() => Array.from(new Set(services.map((s) => s.vehicle))).sort(), [services]);

  const filteredServices = useMemo(() => {
    return services.filter((s) => {
      if (vehicle && s.vehicle !== vehicle) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![s.name, s.summary, s.sku].some((f) => (f || "").toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [services, search, vehicle]);

  const filteredProducts = useMemo(() => {
    return products
      // Admin sees ALL products (incl. inactive/discontinued) so they can quote parts they
      // don't normally stock — discontinued ones are marked visually below.
      .filter((p) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return [p.name, p.description, p.sku].some((f) => (f || "").toLowerCase().includes(q));
      });
  }, [products, search]);

  const isServiceSelected = (id) => value.some((v) => v.service_id === id);
  const isProductSelected = (id) => value.some((v) => v.product_id === id);

  const toggleService = (s) => {
    if (isServiceSelected(s.id)) {
      onChange(value.filter((v) => v.service_id !== s.id));
    } else {
      onChange([...value, {
        service_id: s.id, product_id: "", kind: "service",
        sku: s.sku, name: s.name,
        price: Number(s.price || 0), cost: Number(s.cost || 0), qty: 1,
      }]);
    }
  };

  const toggleProduct = (p) => {
    if (isProductSelected(p.id)) {
      onChange(value.filter((v) => v.product_id !== p.id));
    } else {
      onChange([...value, {
        service_id: "", product_id: p.id, kind: "part",
        sku: p.sku, name: p.name,
        price: Number(p.price || 0), cost: Number(p.cost || 0), qty: 1,
      }]);
    }
  };

  const updateLine = (idx, patch) => {
    onChange(value.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };
  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const total = value.reduce((sum, v) => sum + Number(v.price || 0) * Number(v.qty || 1), 0);
  const totalCost = value.reduce((sum, v) => sum + Number(v.cost || 0) * Number(v.qty || 1), 0);
  const margin = total - totalCost;

  const containerCls = isDark ? "" : "surface-card p-4 sm:p-5";

  return (
    <div className={containerCls} data-testid="quote-picker">
      {isDark && (
        <div className="flex gap-1.5 border-b border-spark-line mb-3">
          <button type="button" onClick={() => setTab("services")} className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-display uppercase tracking-wider font-bold border-b-2 -mb-px transition ${tab === "services" ? "text-spark-ink border-spark-yellow" : "text-spark-slate border-transparent hover:text-spark-ink"}`} data-testid="picker-tab-services">
            <Wrench className="size-3.5" /> Services <span className="opacity-60">{filteredServices.length}</span>
          </button>
          <button type="button" onClick={() => setTab("parts")} className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-display uppercase tracking-wider font-bold border-b-2 -mb-px transition ${tab === "parts" ? "text-spark-ink border-spark-yellow" : "text-spark-slate border-transparent hover:text-spark-ink"}`} data-testid="picker-tab-parts">
            <Package className="size-3.5" /> Parts &amp; products <span className="opacity-60">{filteredProducts.length}</span>
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-12 gap-2 mb-3">
        <div className={`relative ${tab === "services" ? "sm:col-span-7" : "sm:col-span-12"}`}>
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-spark-slate" />
          <input
            placeholder={tab === "services" ? "Search services…" : "Search parts…"}
            className="input-style pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="quote-picker-search"
          />
        </div>
        {tab === "services" && (
          <select className="input-style sm:col-span-5" value={vehicle} onChange={(e) => setVehicle(e.target.value)} data-testid="quote-picker-vehicle">
            <option value="">All vehicles</option>
            {vehicles.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
      </div>

      <div className="border border-spark-line rounded-md max-h-72 overflow-y-auto bg-white" data-testid="quote-picker-list">
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="size-4 animate-spin inline text-spark-teal" /></div>
        ) : tab === "services" ? (
          filteredServices.length === 0 ? (
            <p className="p-6 text-center text-sm text-spark-slate">No services match.</p>
          ) : (
            <ul className="divide-y divide-spark-line">
              {filteredServices.map((s) => {
                const selected = isServiceSelected(s.id);
                return (
                  <li key={s.id}>
                    <button type="button" onClick={() => toggleService(s)}
                      className={`w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-spark-teal-light/40 transition ${selected ? "bg-spark-teal-light" : ""}`}
                      data-testid={`quote-pick-${s.sku}`}>
                      <span className={`size-5 rounded-sm border flex items-center justify-center shrink-0 mt-0.5 ${selected ? "bg-spark-teal border-spark-teal text-white" : "border-spark-line bg-white text-transparent"}`}>
                        {selected ? <Minus className="size-3" /> : <Plus className="size-3 text-spark-slate" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display uppercase tracking-tight font-bold text-sm text-spark-ink">{s.name}</span>
                          <span className="label-mono text-spark-teal text-[10px]">{s.vehicle}</span>
                        </div>
                        <p className="text-xs text-spark-slate line-clamp-1">{s.summary}</p>
                      </div>
                      <span className="font-display uppercase tracking-tight font-bold text-sm text-spark-ink shrink-0">{formatServicePrice(s)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )
        ) : (
          filteredProducts.length === 0 ? (
            <p className="p-6 text-center text-sm text-spark-slate">No parts match.</p>
          ) : (
            <ul className="divide-y divide-spark-line">
              {filteredProducts.map((p) => {
                const selected = isProductSelected(p.id);
                const isOnOrder = p.availability === "on_order";
                const outOfStock = !isOnOrder && p.track_stock && p.stock <= 0;
                return (
                  <li key={p.id}>
                    <button type="button" onClick={() => toggleProduct(p)}
                      className={`w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-spark-teal-light/40 transition ${selected ? "bg-spark-teal-light" : ""}`}
                      data-testid={`quote-pick-product-${p.sku}`}>
                      <span className={`size-5 rounded-sm border flex items-center justify-center shrink-0 mt-0.5 ${selected ? "bg-spark-teal border-spark-teal text-white" : "border-spark-line bg-white text-transparent"}`}>
                        {selected ? <Minus className="size-3" /> : <Plus className="size-3 text-spark-slate" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display uppercase tracking-tight font-bold text-sm text-spark-ink">{p.name}</span>
                          <span className="label-mono text-spark-teal text-[10px]">{p.category}</span>
                          {isOnOrder && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-yellow-200 text-spark-ink">On order · {p.lead_time_days || 7}d</span>}
                          {p.availability === "discontinued" && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-600">Discontinued</span>}
                          {!p.active && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500">Hidden from shop</span>}
                          {outOfStock && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">Out</span>}
                          {!isOnOrder && p.track_stock && p.stock > 0 && p.stock <= p.low_stock_threshold && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-700">Low · {p.stock}</span>}
                        </div>
                        <p className="text-[11px] text-spark-slate font-mono">{p.sku} · cost {moneyN(p.cost)}{p.track_stock && !isOnOrder ? ` · ${p.stock} in stock` : ""}</p>
                      </div>
                      <span className="font-display uppercase tracking-tight font-bold text-sm text-spark-ink shrink-0">{moneyN(p.price)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )
        )}
      </div>

      {/* Selected list */}
      {value.length > 0 && (
        <div className="mt-4 border border-spark-teal rounded-md overflow-hidden bg-white" data-testid="quote-picker-selected">
          <div className="bg-spark-teal-light px-3 py-2 flex items-center justify-between">
            <span className="label-mono text-spark-teal-dark">{value.length} selected</span>
            <button type="button" onClick={() => onChange([])} className="label-mono text-spark-slate hover:text-red-500" data-testid="quote-picker-clear">Clear all</button>
          </div>
          <ul className="divide-y divide-spark-line">
            {value.map((v, i) => {
              if (v.kind === "discount") {
                return (
                  <li key={i} className="px-3 py-2 flex items-center gap-2 sm:gap-3 text-sm bg-spark-yellow/10" data-testid={`quote-discount-line-${i}`}>
                    <span className="shrink-0 size-6 rounded-sm flex items-center justify-center bg-spark-yellow text-spark-ink"><Zap className="size-3.5" /></span>
                    <span className="flex-1 min-w-0 truncate text-spark-ink">{v.name} <span className="text-[10px] text-spark-slate uppercase tracking-wider">· auto, recalculated on save</span></span>
                    <span className="font-display uppercase tracking-tight font-bold text-emerald-700 shrink-0 w-20 text-right">-{moneyN(Math.abs(v.price))}</span>
                    <span className="w-4" />
                  </li>
                );
              }
              const isPart = v.kind === "part" || !!v.product_id;
              const lineTotal = Number(v.price || 0) * Number(v.qty || 1);
              return (
                <li key={i} className="px-3 py-2 flex items-center gap-2 sm:gap-3 text-sm">
                  <span className={`shrink-0 size-6 rounded-sm flex items-center justify-center ${isPart ? "bg-amber-100 text-amber-700" : "bg-spark-teal-light text-spark-teal"}`} title={isPart ? "Part / product" : "Service"}>
                    {isPart ? <Package className="size-3.5" /> : <Wrench className="size-3.5" />}
                  </span>
                  <span className="flex-1 min-w-0 truncate text-spark-ink">{v.name}</span>
                  {isDark && (
                    <>
                      <input
                        type="number" min="1" step="1"
                        className="w-12 rounded-sm border border-spark-line px-1.5 py-1 text-xs text-spark-ink text-center"
                        value={v.qty || 1}
                        onChange={(e) => updateLine(i, { qty: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                        title="Quantity"
                        data-testid={`quote-line-qty-${i}`}
                      />
                      <input
                        type="number" min="0" step="0.01"
                        className="w-20 rounded-sm border border-spark-line px-1.5 py-1 text-xs text-spark-ink text-right font-mono"
                        value={v.price ?? 0}
                        onChange={(e) => updateLine(i, { price: parseFloat(e.target.value || "0") })}
                        title="Unit price"
                        data-testid={`quote-line-price-${i}`}
                      />
                    </>
                  )}
                  <span className="font-display uppercase tracking-tight font-bold text-spark-ink shrink-0 w-20 text-right">{moneyN(lineTotal)}</span>
                  <button type="button" onClick={() => removeAt(i)} className="text-spark-slate hover:text-red-500" data-testid={`quote-picker-remove-${i}`}>
                    <X className="size-4" />
                  </button>
                </li>
              );
            })}
          </ul>
          {isDark && (
            <div className="bg-white px-3 py-2 border-t border-spark-line text-xs text-spark-slate flex items-center justify-between">
              <span>Cost basis: <span className="font-mono text-spark-ink">{moneyN(totalCost)}</span></span>
              <span>Margin: <span className={`font-mono font-bold ${margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{moneyN(margin)}</span></span>
            </div>
          )}
          <div className="bg-spark-teal text-white px-3 py-3 flex items-center justify-between">
            <span className="label-mono text-spark-yellow">Estimated total</span>
            <span className="font-display uppercase tracking-tight text-2xl font-bold" data-testid="quote-picker-total">{moneyN(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotePicker;
